import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useBodyScrollLock } from '../utils/useBodyScrollLock';
import { useAutoFocus } from '../utils/useAutoFocus';
import { useIsMobile } from '../utils/useIsMobile';
import { useFocusTrap } from '../utils/useFocusTrap';
import { useSwipe } from '../animations/Swipe/useSwipe';
import { ModalType } from './tools/ModalCore.const';
import { ModalCoreProps } from './tools/ModalCore.model';
import { ModalHeader } from './components/ModalHeader/ModalHeader';

import useStyles from './ModalCore.style';

export { ModalType };
export type { ModalCoreProps };

const ModalCore = forwardRef<HTMLButtonElement | null, ModalCoreProps>(({
  handleClose,
  handleBack,
  children,
  className,
  headerClassName,
  title,
  type = ModalType.withBackBtn,
  customZIndex,
  titleType = 'center',
  closeButtonClassName,
  bodyScrollLockDisabled = false,
  modalContentId,
  disableFocusTrap = false,
  enableSwipeToClose = false,
  renderBackButton,
  renderMobileTopBar,
  backButtonLabel = 'Back',
  closeButtonAriaLabel = 'Close',
  isMobileApp = false,
  onBackButtonKeyDown
}, ref) => {
  const c = useStyles({ type, titleType, customZIndex, isMobileApp });
  const isMobile = useIsMobile();

  useBodyScrollLock(bodyScrollLockDisabled);
  const focusTrapContainerRef = useFocusTrap(handleClose, !disableFocusTrap);
  const autoFocusRef = useAutoFocus<HTMLButtonElement>();

  const { motionProps } = useSwipe(
    {
      enabled: enableSwipeToClose && isMobile,
    },
    {
      onThresholdReached: () => {
        handleClose();
      },
    }
  );

  const modalContentProps = {
    ref: disableFocusTrap ? undefined : focusTrapContainerRef,
    className: clsx(c.root, className),
    id: modalContentId,
  };

  const innerContent = (
    <>
      <ModalHeader
        type={type}
        title={title}
        titleType={titleType}
        handleClose={handleClose}
        handleBack={handleBack}
        renderBackButton={renderBackButton}
        backButtonLabel={backButtonLabel}
        closeButtonAriaLabel={closeButtonAriaLabel}
        onBackButtonKeyDown={onBackButtonKeyDown}
        autoFocusRef={autoFocusRef}
        forwardedRef={ref}
        closeButtonClassName={closeButtonClassName}
        headerClassName={headerClassName}
        classes={c}
      />
      {children}
    </>
  );

  if (isMobile && enableSwipeToClose) {
    return (
      <>
        {renderMobileTopBar?.()}
        <AnimatePresence>
          <div className={c.modalContainer}>
            <div className={c.modalBackground} />
            <motion.div
              {...modalContentProps}
              {...motionProps}
              ref={modalContentProps.ref}
              exit="exit"
            >
              {innerContent}
            </motion.div>
          </div>
        </AnimatePresence>
      </>
    );
  }

  return (
    <>
      {renderMobileTopBar?.()}
      <div {...modalContentProps}>
        {innerContent}
      </div>
    </>
  );
});

ModalCore.displayName = 'ModalCore';

export default ModalCore;
